import React, { useMemo, type FC } from 'react';
import { cssMap } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

import ChangesIcon from '@atlaskit/icon/core/changes';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import Heading from '@atlaskit/heading';
import StatusWorkflowInReviewIcon from '@atlaskit/icon-lab/core/status-workflow-in-review';
import { Text, Stack, Inline, Pressable } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';

import { useApprovals } from '@confluence/approvals/entry-points/ApprovalsContext.ts';
import type {
	ApprovalStatus,
	WorkflowStatus,
} from '@confluence/approvals/entry-points/ApprovalsContext.ts';
import {
	APPROVALS_PANE_ID,
	APPROVALS_PANE_WIDTH,
} from '@confluence/approvals/entry-points/approvalsPaneSidebar.ts';
import { useRightSidebarContext } from '@confluence/page-layout-context';

import type { ContentState } from '../EditorContentState/types.ts';
import { ContentStateColor } from '../ContentStateColor.tsx';

import { getRelativeTimeAgo } from './getRelativeTimeAgo.ts';
import { UserUpdateInfo, type UserUpdateInfoProps } from './UserUpdateInfo.tsx';
import { StartApprovalButton } from './ApprovalStatusButton/StartApprovalButton.tsx';
import {
	InProgressApprovalButton,
	InProgressApprovalButtonVariant,
} from './ApprovalStatusButton/InProgressApprovalButton.tsx';

const styles = cssMap({
	infoWrapper: {
		width: '360px',
		backgroundColor: token('elevation.surface.overlay'),
		border: `${token('border.width')} solid ${token('color.border')}`,
		boxShadow: token('elevation.shadow.overlay'),
	},
	statusInfo: {
		padding: token('space.200'),
	},
	updateMetadata: {
		flexWrap: 'wrap',
	},
	changeBtn: {
		width: '100%',
		backgroundColor: token('color.background.neutral.subtle'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered'),
		},
		padding: token('space.200'),
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
	},
});

const i18n = defineMessages({
	changeStatus: {
		id: 'content-state.contentStateInfoPopup.change-status-button.text',
		defaultMessage: 'Change status',
		description: 'Text for button which opens the Status selection menu',
	},
	updatedByUser: {
		id: 'content-state.contentStateInfoPopup.updated-by-user',
		defaultMessage: 'Updated {time} by',
		description:
			'Shows when a status was last updated with relative time (including "ago"). User component is rendered separately.',
	},
	updated: {
		id: 'content-state.contentStateInfoPopup.updated',
		defaultMessage: 'Updated {time}',
		description: 'Shows when a status was last updated with relative time (including "ago") only',
	},
	updatedOnByUser: {
		id: 'content-state.contentStateInfoPopup.updated-on-by-user',
		defaultMessage: 'Updated on {date} by',
		description:
			'Shows when a status was last updated with absolute date and user who updated it (for dates older than 7 days). User component is rendered separately.',
	},
	updatedOn: {
		id: 'content-state.contentStateInfoPopup.updated-on',
		defaultMessage: 'Updated on {date}',
		description:
			'Shows when a status was last updated with absolute date only (for dates older than 7 days)',
	},
	updatedByUserOnly: {
		id: 'content-state.contentStateInfoPopup.updated-by-user-only',
		defaultMessage: 'Updated by',
		description:
			'Shows when a user updated a status (when time data is not available). User component is rendered separately.',
	},
});

type ContentStateInfoPopupProps = {
	contentState: ContentState | null;
	activeWorkflowStatus?: WorkflowStatus | null;
	updatedBy?: UserUpdateInfoProps;
	updatedAt?: string;
	loading?: boolean;
	onChangeStatus?: () => void;
	canEdit?: boolean;
	contentType?: 'page' | 'blogpost' | null;
	isViewMode?: boolean;
};

export const ContentStateInfoPopup: FC<ContentStateInfoPopupProps> = ({
	contentState,
	activeWorkflowStatus,
	updatedBy,
	updatedAt,
	loading = false,
	onChangeStatus,
	canEdit = true,
	contentType,
	isViewMode = false,
}) => {
	const intl = useIntl();
	const { approvalRequest, hasEditPermission, approvalProgress } = useApprovals();

	const approvers = approvalRequest?.review?.approvers;
	const rightSidebarContext = useRightSidebarContext();

	const isActiveReview =
		!!approvalRequest?.review && approvalRequest?.review?.reviewStatus !== 'CANCELED';
	const approvalStatus = approvalRequest?.review?.reviewStatus;

	const approvalCount = useMemo(
		() => (approvers ?? []).filter((a) => a.status === 'REVIEWER_APPROVED').length,
		[approvers],
	);

	const displayName = activeWorkflowStatus?.name ?? contentState?.name;
	const timeData = updatedAt ? getRelativeTimeAgo({ lastRelevantDate: updatedAt, intl }) : null;

	const approvalStatusToVariant: Partial<Record<ApprovalStatus, InProgressApprovalButtonVariant>> =
		{
			PENDING: InProgressApprovalButtonVariant.IN_PROGRESS,
			APPROVED: InProgressApprovalButtonVariant.APPROVED,
			DENIED: InProgressApprovalButtonVariant.DECLINED,
		};

	const StatusButton = () => {
		if (isActiveReview) {
			const variant = approvalStatus ? approvalStatusToVariant[approvalStatus] : undefined;
			if (!variant) {
				return <></>;
			}
			return (
				<InProgressApprovalButton
					approvalCount={approvalCount}
					approverCount={approvalProgress.total || 0}
					variant={variant}
					onClick={() => rightSidebarContext.open(APPROVALS_PANE_ID, APPROVALS_PANE_WIDTH)}
					approvers={approvers ?? []}
				/>
			);
		}

		if (activeWorkflowStatus?.allowsReviews && hasEditPermission) {
			return (
				<StartApprovalButton
					onClick={() => rightSidebarContext.open(APPROVALS_PANE_ID, APPROVALS_PANE_WIDTH)}
				/>
			);
		}

		return null;
	};

	const renderStatusIcon = () => {
		if (activeWorkflowStatus) {
			const WorkflowIcon = activeWorkflowStatus.allowsReviews
				? StatusWorkflowInReviewIcon
				: CheckCircleIcon;
			const iconColor = activeWorkflowStatus.allowsReviews
				? token('color.icon.accent.blue')
				: token('color.icon.accent.lime');
			return <WorkflowIcon label="" color={iconColor} />;
		}
		return <ContentStateColor color={contentState?.color} appearance="contentStateInfoPopup" />;
	};

	return (
		<Stack xcss={styles.infoWrapper} space="space.050">
			{/* Status Header and Metadata Section */}
			<Stack xcss={styles.statusInfo} space="space.100">
				{/* Status Header: Icon + Status Name */}
				<Inline space="space.100" alignBlock="center">
					{renderStatusIcon()}
					<Heading as="h4" size="medium">
						{displayName}
					</Heading>
				</Inline>

				{/* User/Timestamp Metadata - flexible layout */}
				{(timeData || updatedBy || loading) && (
					<Inline
						space="space.050"
						alignBlock="center"
						alignInline={loading ? 'center' : 'start'}
						xcss={styles.updateMetadata}
					>
						{loading ? (
							<Spinner size="small" />
						) : (
							<>
								{/* Case 1: Both time and user data (Relative) */}
								{timeData && updatedBy && timeData.type === 'relative' && (
									<>
										<Text size="small" color="color.text.subtlest">
											{intl.formatMessage(i18n.updatedByUser, {
												time: timeData.value,
											})}
										</Text>
										<UserUpdateInfo {...updatedBy} />
									</>
								)}

								{/* Case 2: Both time and user data (Absolute) */}
								{timeData && updatedBy && timeData.type === 'absolute' && (
									<>
										<Text size="small" color="color.text.subtlest">
											{intl.formatMessage(i18n.updatedOnByUser, {
												date: timeData.value,
											})}
										</Text>
										<UserUpdateInfo {...updatedBy} />
									</>
								)}

								{/* Case 3: Time data only (Relative) */}
								{timeData && !updatedBy && timeData.type === 'relative' && (
									<Text size="small" color="color.text.subtlest">
										{intl.formatMessage(i18n.updated, {
											time: timeData.value,
										})}
									</Text>
								)}

								{/* Case 4: Time data only (Absolute) */}
								{timeData && !updatedBy && timeData.type === 'absolute' && (
									<Text size="small" color="color.text.subtlest">
										{intl.formatMessage(i18n.updatedOn, {
											date: timeData.value,
										})}
									</Text>
								)}

								{/* Case 5: User data only */}
								{!timeData && updatedBy && (
									<>
										<Text size="small" color="color.text.subtlest">
											{intl.formatMessage(i18n.updatedByUserOnly)}
										</Text>
										<UserUpdateInfo {...updatedBy} />
									</>
								)}
							</>
						)}
					</Inline>
				)}
			</Stack>

			{!(isViewMode && contentType === 'blogpost') && <StatusButton />}

			{/* Change Status Button - only shown for users with edit permission, hidden for blog posts in view mode */}
			{canEdit && !(isViewMode && contentType === 'blogpost') && (
				<Pressable xcss={styles.changeBtn} onClick={onChangeStatus}>
					<Text weight="medium">
						<Inline space="space.100" alignBlock="center">
							<ChangesIcon label={intl.formatMessage(i18n.changeStatus)} />
							{intl.formatMessage(i18n.changeStatus)}
						</Inline>
					</Text>
				</Pressable>
			)}
		</Stack>
	);
};
